export interface TypeFilter {
	set: (value: string) => void;
}

/**
 * Wires the tabs and the phone sheet of an .armada-filter; the listing loads the chosen value.
 */
export default function initTypeFilter(scope: HTMLElement, onChoose: (value: string) => void): TypeFilter | null {
	const root = scope.querySelector<HTMLElement>('.armada-filter');

	if (!root) {
		return null;
	}

	const tabs = Array.from(root.querySelectorAll<HTMLAnchorElement>('[data-filter-value]'));
	const current = root.querySelector<HTMLElement>('[data-filter-current]');
	const opener = root.querySelector<HTMLElement>('[data-filter-open]');
	const sheet = root.querySelector<HTMLElement>('[data-filter-sheet]');
	const radios = Array.from(sheet?.querySelectorAll<HTMLInputElement>('input[type="radio"]') ?? []);

	const set = (value: string): void => {
		tabs.forEach((tab) => {
			const on = (tab.dataset.filterValue ?? '') === value;

			tab.classList.toggle('is-active', on);

			if (on) {
				tab.setAttribute('aria-current', 'true');

				if (current) {
					current.textContent = tab.dataset.filterName ?? '';
				}
			} else {
				tab.removeAttribute('aria-current');
			}
		});

		radios.forEach((radio) => {
			radio.checked = radio.value === value;
		});
	};

	tabs.forEach((tab) => {
		tab.addEventListener('click', (event) => {
			event.preventDefault();
			onChoose(tab.dataset.filterValue ?? '');
		});
	});

	if (!sheet || !opener) {
		return { set };
	}

	// Builder rows open their own stacking context; from <body> the sheet covers the header too.
	document.body.appendChild(sheet);

	const close = (): void => {
		sheet.classList.remove('is-open');
		document.documentElement.style.overflow = '';
		document.removeEventListener('keydown', onKey);
		window.setTimeout(() => {
			sheet.hidden = true;
		}, 300);
		opener.focus();
	};

	const onKey = (event: KeyboardEvent): void => {
		if (event.key === 'Escape') {
			close();
		}
	};

	opener.addEventListener('click', () => {
		sheet.hidden = false;
		document.documentElement.style.overflow = 'hidden';
		document.addEventListener('keydown', onKey);
		requestAnimationFrame(() => {
			sheet.classList.add('is-open');
			(radios.find((radio) => radio.checked) ?? radios[0])?.focus();
		});
	});

	sheet.querySelectorAll<HTMLElement>('[data-filter-close]').forEach((button) => {
		button.addEventListener('click', close);
	});

	radios.forEach((radio) => {
		radio.addEventListener('change', () => {
			onChoose(radio.value);
			close();
		});
	});

	return { set };
}
